import { useId, type MutableRefObject } from 'react'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Typography from '@mui/joy/Typography'
import {
    type Control,
    type UseFormClearErrors,
    type UseFormSetValue,
    useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import type { CheckoutFormValues } from '@/components/checkout/page/controller'
import type { CheckoutMode } from '@/components/checkout/page/types'
import { VehicleEntryMode, type UserVehicle } from '@/types'
import type { CheckoutVehicleInfoValues } from '@/types/reservation-checkout'
import CheckoutVehicleInfoForm from './form'
import {
    getSelectedSavedCar,
    hasMissingVehicleNumberPlate,
} from './utils'

type CheckoutVehicleInfoProps = {
    desktop?: boolean
    currentUserCars: UserVehicle[]
    mode?: CheckoutMode
    control: Control<CheckoutFormValues>
    setValue: UseFormSetValue<CheckoutFormValues>
    clearErrors: UseFormClearErrors<CheckoutFormValues>
    summaryValues: CheckoutVehicleInfoValues
    shouldEdit: boolean
    missingPlateDismissed: boolean
    hideActions?: boolean
    requestInProgress?: boolean
    errorMessage?: string | null
    sectionRef?: MutableRefObject<HTMLDivElement | null>
    onEdit: () => void
    onCancel: () => void
    onClear: () => void
    onSave: () => Promise<void>
}

export default function CheckoutVehicleInfo({
    desktop,
    currentUserCars,
    mode = 'sectioned',
    control,
    setValue,
    clearErrors,
    summaryValues,
    shouldEdit,
    missingPlateDismissed,
    hideActions = false,
    requestInProgress = false,
    errorMessage,
    sectionRef,
    onEdit,
    onCancel,
    onClear,
    onSave,
}: CheckoutVehicleInfoProps) {
    const { t } = useTranslation()
    const vehicleSelectLabelId = useId()
    const isSingleSubmit = mode === 'single_submit'
    const vehicleEntryMode = useWatch({
        control,
        name: 'vehicle_entry_mode',
    })
    const vehicleSelection = useWatch({
        control,
        name: 'vehicle_selection',
    })
    const selectedSavedCar = getSelectedSavedCar({
        currentUserCars,
        selection: vehicleSelection,
    })
    const shouldShowCustomInputs = currentUserCars.length === 0 || vehicleSelection === 'custom'

    return (
        <Box
            ref={sectionRef}
            sx={{
                scrollMarginBlock: 'calc(70px + 1rem)',
                display: 'flex',
                flexDirection: 'column',
                gap: {
                    xs: 0.5,
                    sm: 1,
                },
                padding: '0.5rem 1rem 1rem',
                borderRadius: 10,
                border: '1px solid var(--joy-palette-neutral-100)',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        flexGrow: 1,
                        alignItems: 'center',
                    }}
                >
                    <DirectionsCarIcon sx={{ color: '#484848', fontSize: desktop ? '24px' : '20px' }} />
                    <Typography
                        level={desktop ? 'body-lg' :'body-md'}
                        sx={{
                            fontWeight: 600,
                        }}
                    >
                        {t('checkout.vehicleInfo.heading')}
                    </Typography>
                </Box>
                {!shouldEdit ? (
                    <Button
                        variant="soft"
                        onClick={onEdit}
                        sx={{
                            color: 'black',
                            backgroundColor: 'var(--joy-palette-neutral-50)',
                            '&:hover': {
                                color: 'black',
                                backgroundColor: 'var(--joy-palette-neutral-100)',
                            },
                            borderRadius: 7,
                            border: '1px solid var(--joy-palette-neutral-200)',
                            mr: -1,
                        }}
                    >
                        {t('checkout.vehicleInfo.edit')}
                    </Button>
                ) : !isSingleSubmit ? <Box height={40} /> : null}
            </Box>
            {shouldEdit ? (
                <CheckoutVehicleInfoForm
                    control={control}
                    currentUserCars={currentUserCars}
                    vehicleEntryMode={vehicleEntryMode}
                    shouldShowCustomInputs={shouldShowCustomInputs}
                    selectedSavedCar={selectedSavedCar}
                    hideActions={hideActions}
                    requestInProgress={requestInProgress}
                    errorMessage={errorMessage}
                    vehicleSelectLabelId={vehicleSelectLabelId}
                    onCancel={onCancel}
                    onClear={onClear}
                    onSubmit={event => {
                        event.preventDefault()
                        void onSave()
                    }}
                    onSaveCurrentSelection={() => {
                        void onSave()
                    }}
                    onVehicleEntryModeChange={mode => {
                        setValue('vehicle_entry_mode', mode)
                        clearErrors(['vehicle_selection', 'number_plate'])
                    }}
                />
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                    }}
                >
                    {!hasMissingVehicleNumberPlate(summaryValues) ? (
                        <Typography
                            level={desktop ? 'h4' : 'body-lg'}
                            sx={{
                                fontWeight: 600,
                            }}
                        >
                            {summaryValues.number_plate}
                        </Typography>
                    ) : null}
                    {summaryValues.car_color ? (
                        <Typography level={desktop ? 'body-md' : 'body-sm'}>
                            {summaryValues.car_color}
                        </Typography>
                    ) : null}
                    {hasMissingVehicleNumberPlate(summaryValues) ? (
                        <Typography
                            level={desktop ? 'body-md' : 'body-sm'}
                            sx={{
                                color: '#6e6e6e',
                                fontWeight: 500,
                            }}
                        >
                            {t('checkout.vehicleInfo.addLaterSelected')}
                        </Typography>
                    ) : null}
                </Box>
            )}
        </Box>
    )
}
